import { watch } from 'vue'

import { useBillingStore } from '@/stores/BillingStore'
import { safeCapture } from '@/helpers/posthog'
import { POSTHOG_EVENTS } from '@/constants/posthog'
import { INSIGHTS_ENTRY_POINTS } from '@/constants'

const REPORT_ROUTE = 'InsightsReport'

// Where a report was opened from, by the route the dealer was on. The sidebar leads to the overview, so
// "nav" is a report opened from there; anything else — a typed URL, a reload, a link from another page — is a
// deep link.
const ENTRY_POINT_BY_ROUTE = {
  Insights: INSIGHTS_ENTRY_POINTS.NAV,
  Homepage: INSIGHTS_ENTRY_POINTS.HOME_CARD,
}

// How long a view waits for the plan to be known (the config loads after the first navigation of a page
// load) before it goes out with "unknown" rather than not at all.
const PLAN_WAIT_MS = 10000

// The entry point of the report being read: set when the dealer comes into a report, kept across its tab
// switches, so every view in one visit says how that visit began.
let entryPoint = INSIGHTS_ENTRY_POINTS.DEEP_LINK

const captureWithPlan = (properties) => {
  const billingStore = useBillingStore()

  const send = (plan) => safeCapture(POSTHOG_EVENTS.INSIGHTS_VIEWED, { ...properties, plan: plan ?? 'unknown' })

  if (billingStore.currentPlanName) {
    send(billingStore.currentPlanName)
    return
  }

  let timer = null
  const stop = watch(() => billingStore.currentPlanName, (plan) => {
    if (!plan) return
    clearTimeout(timer)
    stop()
    send(plan)
  })

  timer = setTimeout(() => {
    stop()
    send(null)
  }, PLAN_WAIT_MS)
}

/**
 * `insights_viewed`, once per report tab the dealer lands on: opening a report, or switching its tab. Hooked on
 * the router's `afterEach`, so it counts navigations — never re-renders, a sort, a page or a search, which
 * don't change the URL's tab. A report URL without a tab fires nothing: the report replaces it with its first
 * tab at once, and that navigation is the view.
 *
 * Not behind the Insights flag: whoever reaches a report is counted.
 */
export const trackInsightsViewed = (to, from) => {
  if (to.name !== REPORT_ROUTE) return

  if (from.name !== REPORT_ROUTE) {
    entryPoint = ENTRY_POINT_BY_ROUTE[from.name] ?? INSIGHTS_ENTRY_POINTS.DEEP_LINK
  }

  const { reportSlug, insightSlug } = to.params
  if (!insightSlug) return

  const sameTab = from.name === REPORT_ROUTE
    && from.params.reportSlug === reportSlug && from.params.insightSlug === insightSlug
  if (sameTab) return

  captureWithPlan({ report: `${reportSlug}/${insightSlug}`, entry_point: entryPoint })
}
